import { useConfig } from "@/components/config/configContext";
import {
  OrgUserPrivate,
  OrgUserRef,
  PreferenceKey,
  PreferenceType,
  UserPrivate,
  useViaductApiNoAuth,
} from "@/viaduct-api";
import Keycloak, { KeycloakProfile } from "keycloak-js";
import { PropsWithChildren, useEffect, useState } from "react";
import { AuthContext } from "./AuthContext";

const LS_SESSION_ORG_USER_ID = "session-org-user-id";

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [keycloak, setKeycloak] = useState<Keycloak>();
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [keycloakProfile, setKeycloakProfile] = useState<KeycloakProfile>();
  const [orgUser, setOrgUser] = useState<OrgUserPrivate>();
  const [user, setUser] = useState<UserPrivate>();
  const [authenticating, setAuthenticating] = useState(false);
  const [fetchingUser, setFetchingUser] = useState(false);
  const { config } = useConfig();
  const client = useViaductApiNoAuth();

  useEffect(() => {
    if (config) {
      const { url, realm, client_id: clientId } = config.auth;
      setKeycloak(new Keycloak({ url, realm, clientId }));
    }
  }, [config]);

  const getToken = async () => {
    if (keycloak) {
      if (keycloak.isTokenExpired()) {
        try {
          console.log("Refreshing token");
          await keycloak.updateToken(30);
        } catch (error) {
          console.error("Failed to refresh the token", error);
          keycloak.logout();
          return null;
        }
      }
      return keycloak.token ?? null;
    }
    return null;
  };

  const getUserData = async () => {
    if (!authenticated) {
      return;
    }

    setFetchingUser(true);

    const userResult = await client.userMeGet({
      headers: { Authorization: `Bearer ${await getToken()}` },
    });

    setFetchingUser(false);

    if (userResult.error) {
      console.error("Failed to retrieve user details", userResult.error);
    }

    setUser(userResult.data);
  };

  const handleInitialOrg = () => {
    if (!user) {
      return;
    }

    const sessionOrgUserId = localStorage.getItem(LS_SESSION_ORG_USER_ID);
    const sessionOrgUser = user.org_users.find(
      (orgUser) => orgUser.id === sessionOrgUserId,
    );

    if (sessionOrgUser) {
      return sessionOrgUser;
    }

    const loginBehaviorPref = user.login_preferences.find(
      (p) => p.key === PreferenceKey.LOGIN_BEHAVIOR,
    );
    if (!loginBehaviorPref) {
      if (user.org_users.length > 0) {
        return user.org_users[0];
      }
      return;
    }

    if (loginBehaviorPref.value === "PRIMARY") {
      if (user.org_users.length > 0) {
        return user.org_users[0];
      }
    }

    if (loginBehaviorPref.value === "LAST USED") {
      const lastOrgPref = user.login_preferences.find(
        (p) => p.key === PreferenceKey.LAST_ORG,
      );
      const lastOrgUser = user.org_users.find(
        (orgUser) => orgUser.id === lastOrgPref?.value,
      );
      if (lastOrgUser) {
        return lastOrgUser;
      }
    }

    // Fall back to using the user's primary org
    if (user.org_users.length > 0) {
      return user.org_users[0];
    }

    // TODO: figure out how to handle a user not having any orgs
  };

  const switchOrg = async (orgUser: OrgUserRef) => {
    await doSwitchOrg(orgUser);

    await client.orgPreferencesSetPreferences({
      headers: { Authorization: `Bearer ${await getToken()}` },
      path: { org_id: orgUser.org.id },
      body: {
        preference_type: PreferenceType.USER,
        preferences: [
          {
            key: PreferenceKey.LAST_ORG,
            value: orgUser.org.id,
          },
        ],
      },
    });

    localStorage.setItem(LS_SESSION_ORG_USER_ID, orgUser.id);
  };

  const doSwitchOrg = async (orgUser: OrgUserRef) => {
    const result = await client.orgUserMeGet({
      path: { org_id: orgUser.org.id },
    });
    if (result.error) {
      //TODO
    }
    if (result.data) {
      setOrgUser(result.data);
    }
  };

  useEffect(() => {
    if (!keycloak) {
      return;
    }

    setAuthenticating(true);

    keycloak
      .init({ checkLoginIframe: false, onLoad: "check-sso" })
      .then((authenticated: boolean) => {
        setAuthenticated(authenticated);
        setKeycloakProfile(keycloak?.profile);
        setInitialized(true);
      })
      .catch((err: Error) => {
        setInitialized(false);
        console.error("Failed to initialize Keycloak", err);
      })
      .finally(() => {
        setAuthenticating(false);
      });
  }, [keycloak]);

  useEffect(() => {
    if (authenticated) {
      getUserData();
    }
  }, [authenticated]);

  useEffect(() => {
    if (user) {
      const orgUserRef = handleInitialOrg();

      if (orgUserRef) {
        doSwitchOrg(orgUserRef);
      }
    }
  }, [user?.id]);

  const logout = async () => {
    if (keycloak) {
      await keycloak.logout({ redirectUri: window.location.origin });
    }
  };

  const login = () => {
    if (keycloak) {
      localStorage.removeItem(LS_SESSION_ORG_USER_ID);
      return keycloak.login();
    }

    return Promise.reject("Keycloak is not initialized");
  };

  return (
    <AuthContext.Provider
      value={{
        keycloak,
        initialized,
        authenticated,
        authenticating,
        keycloakProfile,
        fetchingUser,
        user,
        orgUser,
        logout,
        login,
        switchOrg,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
